import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    MapPin,
    Calendar,
    Users,
    Navigation,
    Search,
    Locate,
    Loader2,
    ExternalLink,
    ZoomIn,
    ZoomOut,
    Move
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { LocationAutocomplete } from './ui/location/location-autocomplete';
import { LocationFeature } from '../types/location';
import { Activity, ActivityType } from '../types/activity';
import { apiService } from '../services/api.service';
import { toast } from 'sonner';


// Default map center (West LA)
const DEFAULT_CENTER: [number, number] = [34.0522, -118.4437];

// Simple OSM raster style (works without API keys; good fallback)
const OSM_STYLE = {
    version: 8 as const,
    sources: {
        osm: {
            type: 'raster' as const,
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors'
        }
    },
    layers: [
        {
            id: 'osm',
            type: 'raster' as const,
            source: 'osm'
        }
    ]
};

// Prefer MapTiler vector style when a key is available, else fallback to OSM raster
const mapTilerKey = process.env.REACT_APP_MAPTILER_KEY;
const MAP_STYLE: any = mapTilerKey
    ? `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`
    : OSM_STYLE;

export function MapView() {
    const navigate = useNavigate();
    const [allActivities, setAllActivities] = useState<Activity[]>([]);
    const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
    const [visibleActivities, setVisibleActivities] = useState(allActivities);
    const [searchTerm, setSearchTerm] = useState('');
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] });
    const [zoomLevel, setZoomLevel] = useState(12);
    const [hoveredActivity, setHoveredActivity] = useState<string | null>(null);
    const [searchLocation, setSearchLocation] = useState<LocationFeature | null>(null);
    const mapRef = useRef<any | null>(null);


    const fetchAllActivities = async () => {
        try {
            const activities = await apiService.getAllActivities();
            setAllActivities(activities);
        } catch (error) {
            console.error('Failed to fetch activities:', error);
            toast.error('Failed to load activities. Please try again.');
        }
    }

    const handleLocationSearch = (location: LocationFeature) => {
        setSearchLocation(location);
        const newCenter = {
            lat: location.center[1],
            lng: location.center[0]
        };
        setMapCenter(newCenter);

        try {
            const map = mapRef.current?.getMap();
            map?.flyTo({
                center: [location.center[0], location.center[1]],
                zoom: 15,
                essential: true
            });
        } catch (_) { /* no-op */ }

        updateVisibleActivities();
    };

    // Calculate distance between two coordinates
    const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
        const R = 3959; // Radius of the Earth in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
    };

    // Update visible activities based on current map bounds and search
    const updateVisibleActivities = useCallback(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;

        const bounds = map.getBounds();
        const zoom = map.getZoom();
        const center = map.getCenter();

        const activitiesInView = allActivities.filter(activity => {
            const inBounds = bounds.contains({ lng: activity.location?.longitude, lat: activity.location?.latitude } as any);
            const matchesSearch = searchTerm === '' ||
                activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                activity.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                activity.location?.name.split(',')[0].toLowerCase().includes(searchTerm.toLowerCase());
            return inBounds && matchesSearch;
        });

        const activitiesWithDistance = activitiesInView.map(activity => {
            if (userLocation) {
                const distance = calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    activity.location?.latitude || 0,
                    activity.location?.longitude || 0
                );
                return { ...activity, distance: `${distance.toFixed(1)} miles` };
            }
            return activity;
        });

        if (userLocation) {
            activitiesWithDistance.sort((a, b) =>
                parseFloat(a.distance?.split(' ')[0] || '0') - parseFloat(b.distance?.split(' ')[0] || '0')
            );
        }

        setVisibleActivities(activitiesWithDistance);
        setZoomLevel(zoom);
        setMapCenter({ lat: center.lat, lng: center.lng });
    }, [searchTerm, userLocation, allActivities]);

    // Handle finding user location
    const handleLocateUser = () => {
        setIsLoadingLocation(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setUserLocation(newLocation);
                    // Update map view
                    try {
                        const map = mapRef.current?.getMap();
                        map?.flyTo({ center: [newLocation.lng, newLocation.lat], zoom: 15, essential: true });
                    } catch (_) { /* no-op */ }
                    setMapCenter(newLocation);
                    setZoomLevel(15);
                    setIsLoadingLocation(false);
                    updateVisibleActivities();
                },
                (error) => {
                    // Silently handle geolocation errors - this is expected when permission is denied
                    setIsLoadingLocation(false);
                    // Fallback to default center
                    const fallbackLocation = { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] };
                    setUserLocation(fallbackLocation);
                    setMapCenter(fallbackLocation);
                    try {
                        const map = mapRef.current?.getMap();
                        map?.jumpTo({ center: [fallbackLocation.lng, fallbackLocation.lat], zoom: 12 });
                    } catch (_) { /* no-op */ }
                    updateVisibleActivities();
                },
                {
                    timeout: 10000,
                    enableHighAccuracy: false,
                    maximumAge: 300000 // 5 minutes
                }
            );
        } else {
            // Silently fallback when geolocation is not supported
            setIsLoadingLocation(false);
            // Fallback to default center
            const fallbackLocation = { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] };
            setUserLocation(fallbackLocation);
            setMapCenter(fallbackLocation);
            try {
                const map = mapRef.current?.getMap();
                map?.jumpTo({ center: [fallbackLocation.lng, fallbackLocation.lat], zoom: 12 });
            } catch (_) { /* no-op */ }
            updateVisibleActivities();
        }
    };

    // Handle zoom buttons
    const handleZoom = (direction: 'in' | 'out') => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        if (direction === 'in') map.zoomIn(); else map.zoomOut();
    };

    // Fetch all activities on mount
    useEffect(() => {
        fetchAllActivities();
    }, []);

    // Re-filter when search term changes
    useEffect(() => {
        if (allActivities.length > 0 && mapRef.current?.getMap()) {
            updateVisibleActivities();
        }
    }, [searchTerm, allActivities]);

    // Try to get user location on mount
    useEffect(() => {
        handleLocateUser();
    }, []);

    const handleActivityClick = (activityId: string) => {
        navigate(`/activity/${activityId}`);
    };

    const handleActivityHover = (activityId: string | null) => {
        setHoveredActivity(activityId);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6">
                <h1 className="text-3xl font-bold mb-2">Activities Near You</h1>
                <p className="text-muted-foreground">
                    Discover activities happening in your area. Use the interactive map to explore different locations.
                </p>
            </div>

            {/* Search and Controls */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                        placeholder="Search activities, types, or locations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Button
                    onClick={handleLocateUser}
                    variant="outline"
                    disabled={isLoadingLocation}
                    className="flex items-center space-x-2"
                >
                    {isLoadingLocation ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Locate className="w-4 h-4" />
                    )}
                    <span>Find My Location</span>
                </Button>
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Interactive Map (MapLibre via react-map-gl) */}
                <div className="relative h-[500px] rounded-lg overflow-hidden shadow-lg">
                    <Map
                        ref={mapRef}
                        initialViewState={{ latitude: DEFAULT_CENTER[0], longitude: DEFAULT_CENTER[1], zoom: 12 }}
                        style={{ width: '100%', height: '100%' }}
                        mapStyle={MAP_STYLE}
                        onLoad={updateVisibleActivities}
                        onMoveEnd={updateVisibleActivities}
                    >

                        {/* Location Search Autocomplete */}
                        <div className="mb-6 flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <LocationAutocomplete
                                    onSelect={handleLocationSearch}
                                    placeholder="Search activities, locations, or places..."
                                    searchOptions={{
                                        limit: 5,
                                        proximity: userLocation ? {
                                            longitude: userLocation.lng,
                                            latitude: userLocation.lat
                                        } : undefined,
                                        country: ['us'],
                                    }}
                                />
                            </div>
                            <Button
                                onClick={handleLocateUser}
                                variant="outline"
                                disabled={isLoadingLocation}
                                className="flex items-center space-x-2"
                            >
                                {isLoadingLocation ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Locate className="w-4 h-4" />
                                )}
                                <span>Find My Location</span>
                            </Button>
                        </div>

                        {/* Navigation Control, but this is overlapping with the zoom buttons*/}
                        <NavigationControl position="bottom-right" />

                        {/* User Location Marker */}
                        {userLocation && (
                            <Marker latitude={userLocation.lat} longitude={userLocation.lng} anchor="bottom">
                                <div className="relative">
                                    <div className="w-3 h-3 bg-blue-500 border-2 border-white rounded-full shadow" />
                                    <div className="absolute -inset-2 bg-blue-400 rounded-full opacity-30 animate-ping" />
                                </div>
                            </Marker>
                        )}

                        {/* Activity Markers */}
                        {visibleActivities.map(activity => (
                            <Marker
                                key={activity.id}
                                latitude={activity.location?.latitude || 0}
                                longitude={activity.location?.longitude || 0}
                                anchor="bottom"
                                onClick={() => setSelectedActivity(activity.id)}
                            >
                                <div className={`relative transition-all duration-200 ${hoveredActivity === activity.id || selectedActivity === activity.id ? 'scale-110' : 'scale-100'}`}>
                                    <div className={`w-8 h-8 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-sm font-bold ${activity.type === ActivityType.SPORTS_FITNESS ? 'bg-green-500' :
                                        activity.type === ActivityType.SOCIAL ? 'bg-orange-500' :
                                            activity.type === ActivityType.WELLNESS ? 'bg-purple-500' :
                                                activity.type === ActivityType.LEARNING ? 'bg-red-500' :
                                                    activity.type === ActivityType.ENTERTAINMENT ? 'bg-blue-500' :
                                                        activity.type === ActivityType.TRAVEL ? 'bg-cyan-500' :
                                                            'bg-gray-500'
                                        }`}>
                                        {activity.type === ActivityType.SPORTS_FITNESS ? '🎾' :
                                            activity.type === ActivityType.WELLNESS ? '🧘' :
                                                activity.type === ActivityType.LEARNING ? '🧗' :
                                                    activity.type === ActivityType.ENTERTAINMENT ? '🏃' :
                                                        activity.type === ActivityType.TRAVEL ? '🏊' : '📍'}
                                    </div>
                                </div>
                            </Marker>
                        ))}
                    </Map>

                    {/* Map Controls (extra zoom buttons) */}
                    {/* <div className="absolute top-4 right-4 flex flex-col space-y-2 z-10 pointer-events-none">
                        <div className="pointer-events-auto">
                            <Button size="sm" variant="secondary" onClick={() => handleZoom('in')} className="w-8 h-8 p-0">
                                <ZoomIn size={16} />
                            </Button>
                        </div>
                        <div className="pointer-events-auto">
                            <Button size="sm" variant="secondary" onClick={() => handleZoom('out')} className="w-8 h-8 p-0">
                                <ZoomOut size={16} />
                            </Button>
                        </div>
                    </div> */}

                    {/* Zoom Level Indicator */}
                    {/* <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-gray-800/90 rounded px-3 py-1 text-sm">
                        Zoom: {zoomLevel}
                    </div> */}
                </div>

                {/* Activity List */}
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    <div className="flex items-center justify-between sticky top-0 bg-background py-2 border-b">
                        <h2 className="text-xl font-semibold">
                            {visibleActivities.length} Activities in View
                        </h2>
                    </div>

                    {visibleActivities.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <MapPin size={48} className="mx-auto mb-4 opacity-50" />
                            <p>No activities found in this area</p>
                            <p className="text-sm">Try zooming out or adjusting your search</p>
                        </div>
                    ) : (
                        visibleActivities.map(activity => (
                            <Card
                                key={activity.id}
                                id={`activity-${activity.id}`}
                                className={`cursor-pointer transition-all hover:shadow-md ${selectedActivity === activity.id ? 'ring-2 ring-primary' : ''
                                    } ${hoveredActivity === activity.id ? 'bg-accent/50' : ''
                                    }`}
                                onClick={() => handleActivityClick(activity.id)}
                                onMouseEnter={() => handleActivityHover(activity.id)}
                                onMouseLeave={() => handleActivityHover(null)}
                            >
                                <CardContent className="p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h3 className="font-semibold">{activity.title}</h3>
                                            <Badge variant="outline" className="mt-1">
                                                {activity.type}
                                            </Badge>
                                        </div>
                                        {activity.distance && (
                                            <span className="text-sm text-muted-foreground font-medium">
                                                {activity.distance}
                                            </span>
                                        )}
                                    </div>

                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center space-x-2">
                                            <Calendar size={14} className="text-muted-foreground" />
                                            <span>
                                                {new Date(activity.dateTime).toLocaleDateString()}
                                                {" at "}
                                                {
                                                    new Date(activity.dateTime).toLocaleTimeString(
                                                        'en-US', { hour: '2-digit', minute: '2-digit', hour12: true }
                                                    )
                                                }</span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <MapPin size={14} className="text-muted-foreground" />
                                            <span>{activity.location?.name.split(',')[0]}</span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <Users size={14} className="text-muted-foreground" />
                                            <span>{activity.participants}/{activity.maxParticipants} participants</span>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
                                        <span className="font-semibold">
                                            {activity.price === 0 ? 'Free' : `$${activity.price}`}
                                        </span>
                                        <div className="flex space-x-2">
                                            <Button size="sm" className="text-xs">
                                                Apply to Join
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
